import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { GlobalContext } from '@mummssoftware/global-context/GlobalContext';
import { isElectionAddendumCreated } from '@mummssoftware/utils/utils';

import { Context } from '../../context';
import { VisitContext } from '../Context/contexts/VisitContext';
import {
  getVisitElectionAddendumStatus,
  VisitElectionAddendumStatus,
} from './electionAddendumPromptRules';

type VisitId = string | number | null;

type ElectionAddendumContextValue = {
  /** EA state of the open visit; drives both the chip and the auto prompt. */
  status: VisitElectionAddendumStatus;
  /** User opened the prompt from the visit-header EA chip. */
  iconPromptOpen: boolean;
  openFromIcon: () => void;
  closeIconPrompt: () => void;
  /** Visit whose auto prompt was answered. Lives here, not in the prompt,
   * because the prompt unmounts with the visit dialog on minimize. */
  dismissedVisitId: VisitId;
  setDismissedVisitId: (visitId: VisitId) => void;
};

const ElectionAddendumContext = createContext<ElectionAddendumContextValue>({
  status: 'hidden',
  iconPromptOpen: false,
  openFromIcon: () => undefined,
  closeIconPrompt: () => undefined,
  dismissedVisitId: null,
  setDismissedVisitId: () => undefined,
});

/**
 * Fetches the EA due date (with the trigger date) from workflow-server for the
 * open visit. Mounted at Dashboard level so minimize/restore doesn't refetch.
 * Refetches when the patient header's daysRemaining changes (EA note tagged,
 * EA form submitted).
 */
const useVisitRequiredDate = (visitId: VisitId) => {
  const { workflow, agency, patient } = useContext(Context);
  const { patientGlobal } = useContext(GlobalContext);
  // null data = request failed.
  const [requiredDate, setRequiredDate] = useState<{
    visitId: VisitId;
    data: mumms.electionAddendumRequiredDate | null;
  } | null>(null);

  const patientId = String(patient?.patientId ?? patient?.id ?? '');
  const headerDaysRemaining = (patientGlobal ?? patient)?.eaDetails
    ?.daysRemaining;

  useEffect(() => {
    if (visitId == null || !patientId) {
      return undefined;
    }
    let cancelled = false;
    const setData = (data: mumms.electionAddendumRequiredDate | null) => {
      if (!cancelled) {
        setRequiredDate({ visitId, data });
      }
    };
    workflow
      .getElectionAddendumRequiredDate(patientId, agency)
      .then((res) => {
        if (res instanceof Response) {
          console.error('[EA] due-date fetch failed', {
            visitId,
            status: res.status,
          });
          setData(null);
          return;
        }
        setData(res);
      })
      .catch((err) => {
        console.error('[EA] due-date fetch failed', { visitId }, err);
        setData(null);
      });
    return () => {
      cancelled = true;
    };
  }, [visitId, patientId, headerDaysRemaining, workflow, agency]);

  // A response for another visit counts as still loading.
  return requiredDate?.visitId === visitId ? requiredDate.data : undefined;
};

export const ElectionAddendumProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { currentUser } = useContext(Context);
  const { visitOpen, savedVisit } = useContext(VisitContext);
  const { appVisit } = useContext(GlobalContext);
  const [iconPromptOpen, setIconPromptOpen] = useState(false);
  const [dismissedVisitId, setDismissedVisitId] = useState<VisitId>(null);

  const visitId = visitOpen ? savedVisit?.id ?? null : null;
  const requiredDate = useVisitRequiredDate(visitId);
  // Prefer appVisit (forms SUBMIT updates it); fall back to savedVisit.
  const statusVisit = appVisit ?? savedVisit;
  const status =
    visitId == null
      ? 'hidden'
      : getVisitElectionAddendumStatus({
          eaCreated: isElectionAddendumCreated(statusVisit),
          requiredDate,
          visitStartUtc: appVisit?.effectivedatetime ?? savedVisit?.startDate,
          fallbackTimezone: currentUser?.siteTimezone,
        });

  const openFromIcon = useCallback(() => setIconPromptOpen(true), []);
  const closeIconPrompt = useCallback(() => setIconPromptOpen(false), []);

  useEffect(() => {
    if (!visitOpen) {
      setDismissedVisitId(null);
      setIconPromptOpen(false);
    }
  }, [visitOpen]);

  const value = useMemo(
    () => ({
      status,
      iconPromptOpen,
      openFromIcon,
      closeIconPrompt,
      dismissedVisitId,
      setDismissedVisitId,
    }),
    [status, iconPromptOpen, openFromIcon, closeIconPrompt, dismissedVisitId],
  );

  return (
    <ElectionAddendumContext.Provider value={value}>
      {children}
    </ElectionAddendumContext.Provider>
  );
};

export const useElectionAddendum = () => useContext(ElectionAddendumContext);
